// COMP-BODY-MAP (US-CLN-070، 111، 112): نواحی صورت و بدن هم‌سو با BodyAreas سرور (Phoenix.Clinical.Domain.Beauty.BodyAreas) و هندسه SVG ساده
// هر ناحیه. برچسب فارسی هر ناحیه در i18n (cln:body_area.*).

export type BodyMapView = 'face' | 'front' | 'back';

export type RegionShape =
  | { kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number }
  | { kind: 'rect'; x: number; y: number; width: number; height: number; r?: number }
  | { kind: 'path'; d: string };

export interface BodyRegion {
  code: string;
  view: BodyMapView;
  shapes: RegionShape[];
}

/** نواحی صورت (نقشه تزریق و لیزر صورت). */
export const FACE_AREAS = ['forehead', 'glabella', 'periorbital', 'nose', 'cheeks', 'upper_lip', 'lips', 'chin', 'jawline', 'full_face'] as const;

/** نواحی بدن. */
export const BODY_AREAS = [
  'scalp',
  'neck',
  'underarms',
  'arms',
  'forearms',
  'hands',
  'chest',
  'abdomen',
  'back',
  'lower_back',
  'bikini',
  'buttocks',
  'thighs',
  'lower_legs',
  'feet',
] as const;

export const ALL_AREAS: readonly string[] = [...FACE_AREAS, ...BODY_AREAS];

/** ابعاد viewBox هر نما. */
export const VIEW_BOX: Record<BodyMapView, { width: number; height: number }> = {
  face: { width: 200, height: 240 },
  front: { width: 200, height: 400 },
  back: { width: 200, height: 400 },
};

/** طرح کلی (غیرقابل کلیک) هر نما. */
export const OUTLINES: Record<BodyMapView, RegionShape[]> = {
  face: [{ kind: 'ellipse', cx: 100, cy: 122, rx: 78, ry: 104 }],
  front: [
    { kind: 'ellipse', cx: 100, cy: 30, rx: 22, ry: 26 },
    { kind: 'rect', x: 64, y: 70, width: 72, height: 120, r: 18 },
    { kind: 'rect', x: 38, y: 76, width: 22, height: 150, r: 10 },
    { kind: 'rect', x: 140, y: 76, width: 22, height: 150, r: 10 },
    { kind: 'rect', x: 72, y: 186, width: 26, height: 190, r: 10 },
    { kind: 'rect', x: 102, y: 186, width: 26, height: 190, r: 10 },
  ],
  back: [
    { kind: 'ellipse', cx: 100, cy: 30, rx: 22, ry: 26 },
    { kind: 'rect', x: 64, y: 70, width: 72, height: 120, r: 18 },
    { kind: 'rect', x: 38, y: 76, width: 22, height: 150, r: 10 },
    { kind: 'rect', x: 140, y: 76, width: 22, height: 150, r: 10 },
    { kind: 'rect', x: 72, y: 186, width: 26, height: 190, r: 10 },
    { kind: 'rect', x: 102, y: 186, width: 26, height: 190, r: 10 },
  ],
};

/** نواحی قابل انتخاب (ترتیب رسم: بزرگ‌ترها پیش‌تر تا کوچک‌ترها روی آن‌ها قرار گیرند). */
export const REGIONS: BodyRegion[] = [
  { code: 'full_face', view: 'face', shapes: [{ kind: 'ellipse', cx: 100, cy: 122, rx: 78, ry: 104 }] },
  { code: 'forehead', view: 'face', shapes: [{ kind: 'rect', x: 52, y: 32, width: 96, height: 38, r: 14 }] },
  { code: 'glabella', view: 'face', shapes: [{ kind: 'ellipse', cx: 100, cy: 82, rx: 12, ry: 8 }] },
  {
    code: 'periorbital',
    view: 'face',
    shapes: [
      { kind: 'ellipse', cx: 66, cy: 98, rx: 20, ry: 10 },
      { kind: 'ellipse', cx: 134, cy: 98, rx: 20, ry: 10 },
    ],
  },
  { code: 'nose', view: 'face', shapes: [{ kind: 'path', d: 'M100 92 L88 146 L112 146 Z' }] },
  {
    code: 'cheeks',
    view: 'face',
    shapes: [
      { kind: 'ellipse', cx: 58, cy: 140, rx: 20, ry: 24 },
      { kind: 'ellipse', cx: 142, cy: 140, rx: 20, ry: 24 },
    ],
  },
  { code: 'upper_lip', view: 'face', shapes: [{ kind: 'rect', x: 80, y: 152, width: 40, height: 10, r: 4 }] },
  { code: 'lips', view: 'face', shapes: [{ kind: 'ellipse', cx: 100, cy: 174, rx: 22, ry: 8 }] },
  { code: 'chin', view: 'face', shapes: [{ kind: 'ellipse', cx: 100, cy: 204, rx: 24, ry: 14 }] },
  {
    code: 'jawline',
    view: 'face',
    shapes: [
      { kind: 'path', d: 'M34 160 Q44 200 78 214 L82 204 Q52 192 44 158 Z' },
      { kind: 'path', d: 'M166 160 Q156 200 122 214 L118 204 Q148 192 156 158 Z' },
    ],
  },
  { code: 'scalp', view: 'front', shapes: [{ kind: 'ellipse', cx: 100, cy: 16, rx: 20, ry: 12 }] },
  { code: 'neck', view: 'front', shapes: [{ kind: 'rect', x: 90, y: 54, width: 20, height: 16, r: 4 }] },
  {
    code: 'underarms',
    view: 'front',
    shapes: [
      { kind: 'ellipse', cx: 64, cy: 88, rx: 7, ry: 9 },
      { kind: 'ellipse', cx: 136, cy: 88, rx: 7, ry: 9 },
    ],
  },
  { code: 'chest', view: 'front', shapes: [{ kind: 'rect', x: 72, y: 76, width: 56, height: 40, r: 10 }] },
  { code: 'abdomen', view: 'front', shapes: [{ kind: 'rect', x: 74, y: 120, width: 52, height: 50, r: 10 }] },
  {
    code: 'arms',
    view: 'front',
    shapes: [
      { kind: 'rect', x: 40, y: 100, width: 18, height: 52, r: 8 },
      { kind: 'rect', x: 142, y: 100, width: 18, height: 52, r: 8 },
    ],
  },
  {
    code: 'forearms',
    view: 'front',
    shapes: [
      { kind: 'rect', x: 40, y: 156, width: 18, height: 50, r: 8 },
      { kind: 'rect', x: 142, y: 156, width: 18, height: 50, r: 8 },
    ],
  },
  {
    code: 'hands',
    view: 'front',
    shapes: [
      { kind: 'ellipse', cx: 49, cy: 220, rx: 10, ry: 12 },
      { kind: 'ellipse', cx: 151, cy: 220, rx: 10, ry: 12 },
    ],
  },
  { code: 'bikini', view: 'front', shapes: [{ kind: 'rect', x: 80, y: 172, width: 40, height: 20, r: 8 }] },
  {
    code: 'thighs',
    view: 'front',
    shapes: [
      { kind: 'rect', x: 74, y: 196, width: 22, height: 72, r: 8 },
      { kind: 'rect', x: 104, y: 196, width: 22, height: 72, r: 8 },
    ],
  },
  {
    code: 'lower_legs',
    view: 'front',
    shapes: [
      { kind: 'rect', x: 75, y: 272, width: 20, height: 84, r: 8 },
      { kind: 'rect', x: 105, y: 272, width: 20, height: 84, r: 8 },
    ],
  },
  {
    code: 'feet',
    view: 'front',
    shapes: [
      { kind: 'ellipse', cx: 85, cy: 368, rx: 11, ry: 7 },
      { kind: 'ellipse', cx: 115, cy: 368, rx: 11, ry: 7 },
    ],
  },
  { code: 'back', view: 'back', shapes: [{ kind: 'rect', x: 70, y: 76, width: 60, height: 60, r: 12 }] },
  { code: 'lower_back', view: 'back', shapes: [{ kind: 'rect', x: 72, y: 140, width: 56, height: 34, r: 10 }] },
  {
    code: 'buttocks',
    view: 'back',
    shapes: [
      { kind: 'ellipse', cx: 87, cy: 194, rx: 14, ry: 16 },
      { kind: 'ellipse', cx: 113, cy: 194, rx: 14, ry: 16 },
    ],
  },
];

/** نمای اصلی یک ناحیه. */
export function viewOf(code: string): BodyMapView {
  return REGIONS.find((region) => region.code === code)?.view ?? 'front';
}

/** سطح رنگ بر اساس تعداد جلسه (۰ تا ۴). */
export function heatLevel(count: number, max: number): number {
  if (count <= 0 || max <= 0) {
    return 0;
  }
  return Math.min(4, Math.max(1, Math.ceil((count / max) * 4)));
}

/** مختصات نرمال (۰ تا ۱) یک کلیک روی SVG با viewBox. */
export function normalizePoint(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }): { x: number; y: number } {
  const clamp = (value: number) => (Number.isFinite(value) ? Math.min(1, Math.max(0, Math.round(value * 10_000) / 10_000)) : 0.5);
  return { x: clamp((clientX - rect.left) / rect.width), y: clamp((clientY - rect.top) / rect.height) };
}
